import { z } from "zod/v4";
import { OcpiDateTime } from "../ocpi.utils";
import { DisplayText, Price } from "./_common";
import { EnergyMix } from "./locations";

const TariffDimensionType = z.enum(["ENERGY", "FLAT", "PARKING_TIME", "TIME"]);

const PriceComponent = z.object({
  type: TariffDimensionType,
  price: z.number().nonnegative(),
  vat: z.number().nonnegative().nullish(),
  step_size: z.number().int().nonnegative(),
});

const DayOfWeek = z.enum([
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
]);

const ReservationRestrictionType = z.enum([
  "RESERVATION",
  "RESERVATION_EXPIRES",
]);

const TariffRestrictions = z.object({
  start_time: z
    .string()
    .length(5)
    .regex(/([0-1][0-9]|2[0-3]):[0-5][0-9]/)
    .nullish(),
  end_time: z
    .string()
    .length(5)
    .regex(/([0-1][0-9]|2[0-3]):[0-5][0-9]/)
    .nullish(),
  start_date: z
    .string()
    .length(10)
    .regex(/([12][0-9]{3})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])/)
    .nullish(),
  end_date: z
    .string()
    .length(10)
    .regex(/([12][0-9]{3})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])/)
    .nullish(),
  min_kwh: z.number().nonnegative().nullish(),
  max_kwh: z.number().nonnegative().nullish(),
  min_current: z.number().nonnegative().nullish(),
  max_current: z.number().nonnegative().nullish(),
  min_power: z.number().nonnegative().nullish(),
  max_power: z.number().nonnegative().nullish(),
  min_duration: z.number().int().nonnegative().nullish(),
  max_duration: z.number().int().nonnegative().nullish(),
  day_of_week: z.array(DayOfWeek).nullish(),
  reservation: ReservationRestrictionType.nullish(),
});

const TariffElement = z.object({
  price_components: z.array(PriceComponent).nonempty(),
  restrictions: TariffRestrictions.nullish(),
});

const TariffType = z.enum([
  "AD_HOC_PAYMENT",
  "PROFILE_CHEAP",
  "PROFILE_FAST",
  "PROFILE_GREEN",
  "REGULAR",
]);

export const PriceLimit = z.object({
  before_taxes: z.number(),
  after_taxes: z.number().nullish(),
});

export const Tariff = z.object({
  id: z.string().max(36),
  country_code: z.string().length(2),
  party_id: z.string().length(3),
  currency: z.string().length(3),
  type: TariffType.nullish(),
  tariff_alt_text: z.array(DisplayText).nullish(),
  tariff_alt_url: z.url().nullish(),
  min_price: PriceLimit.nullish(),
  max_price: PriceLimit.nullish(),
  preauthorize_amount: z.number().nullish(),
  elements: z.array(TariffElement).nonempty(),
  tax_included: z.enum(["YES", "NO", "N/A"]),
  energy_mix: EnergyMix.nullish(),
  start_date_time: OcpiDateTime.nullish(),
  end_date_time: OcpiDateTime.nullish(),
  last_updated: OcpiDateTime,
});

export const Tariffs = z.array(Tariff);
